import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  X, Loader2, Sparkles, Copy, Check, RefreshCw, Save, Mail, Linkedin, Repeat, TrendingUp,
  BookTemplate, Send, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import DomainBanner from "./DomainBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHANNELS = [
  { id: "email", label: "Cold Email", icon: Mail },
  { id: "linkedin", label: "LinkedIn DM", icon: Linkedin },
  { id: "followup", label: "Follow-up", icon: Repeat },
  { id: "investor", label: "Investor", icon: TrendingUp },
];

export default function OutreachModal({ contact, onClose, onGenerated }) {
  const [channel, setChannel] = useState("email");
  const [instructions, setInstructions] = useState("");
  const [saveActivity, setSaveActivity] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");
  const [templates, setTemplates] = useState({ builtins: [], custom: [] });
  const [showTemplates, setShowTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);
  const [useLong, setUseLong] = useState(false);
  const [senderStatus, setSenderStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/crm/sender-status`)
      .then(({ data }) => { if (!cancelled) setSenderStatus(data); })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, []);

  // Pre-flight sandbox block: when in sandbox AND the contact's email isn't
  // the verified one, we block the Send button and surface a clear warning.
  const sandboxBlocked = !!(
    senderStatus?.sandbox_mode &&
    contact.email &&
    senderStatus.verified_recipients?.length > 0 &&
    !senderStatus.verified_recipients
      .map((e) => e.toLowerCase())
      .includes(contact.email.toLowerCase())
  );

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/crm/outreach-templates`)
      .then(({ data }) => { if (!cancelled) setTemplates(data); })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, []);

  const visibleTemplates = [
    ...templates.builtins.filter((t) => t.channel === channel && (!t.category || t.category === contact.category)),
    ...templates.custom.filter((t) => t.channel === channel && (!t.category || t.category === contact.category)),
  ];

  const generate = async (regenerate = false) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/crm/outreach/generate`, {
        contact_id: contact.id,
        channel,
        instructions: instructions.trim() || null,
        save_activity: saveActivity,
      });
      setResult(data);
      if (saveActivity && onGenerated) onGenerated();
      toast.success(regenerate ? "Regenerated" : "Outreach drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  const canSend = channel !== "linkedin" && !!contact.email && !!result?.subject && !!(result?.short || result?.long) && !sandboxBlocked;

  const sendEmail = async () => {
    if (!canSend) return;
    if (!window.confirm(`Send this email to ${contact.email}?`)) return;
    setSending(true);
    try {
      const body = useLong && result.long ? result.long : result.short;
      const { data } = await axios.post(`${API}/admin/crm/outreach/send`, {
        contact_id: contact.id,
        subject: result.subject,
        body,
        body_format: "text",
        cta: result.cta || null,
        follow_up_days: result.follow_up_days || null,
        channel: channel === "linkedin" ? "email" : channel,
      });
      setSentInfo(data);
      toast.success("Email sent");
      if (onGenerated) onGenerated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="crm-outreach-modal"
    >
      <div
        className="bg-white w-full md:max-w-3xl rounded-t-2xl md:rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between bg-gradient-to-br from-indigo-50 to-cyan-50">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                AI Outreach for {contact.name}
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {contact.organization || "—"} · {contact.category} · {contact.outreach_stage}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 flex-shrink-0"
            data-testid="crm-outreach-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body (scrolls) */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <DomainBanner compact />
          {/* Channel picker */}
          <div>
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Channel</span>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const active = channel === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setChannel(c.id)}
                    data-testid={`crm-outreach-channel-${c.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ring-1 transition ${
                      active
                        ? "bg-indigo-600 text-white ring-indigo-600"
                        : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Instructions */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Extra instructions (optional)
              </span>
              {visibleTemplates.length > 0 && (
                <button
                  onClick={() => setShowTemplates((v) => !v)}
                  className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1"
                  data-testid="crm-outreach-templates-toggle"
                >
                  <BookTemplate className="w-3.5 h-3.5" />
                  Templates ({visibleTemplates.length})
                </button>
              )}
            </div>
            {showTemplates && visibleTemplates.length > 0 && (
              <div
                className="mb-2 bg-slate-50 border border-slate-200 rounded-md divide-y divide-slate-200 max-h-48 overflow-y-auto"
                data-testid="crm-outreach-templates-list"
              >
                {visibleTemplates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => {
                      setInstructions(t.body);
                      setShowTemplates(false);
                      toast.success(`Loaded "${t.name}"`);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-white text-sm transition"
                    data-testid={`crm-outreach-template-${t.id}`}
                  >
                    <div className="font-semibold text-slate-800 text-xs">{t.name}</div>
                    {t.description && (
                      <div className="text-[11px] text-slate-500 mt-0.5">{t.description}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
            <textarea
              rows={3}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g. mention their published research on adolescent screening, propose a 20-min call next week"
              className="w-full text-sm rounded-md border border-slate-300 px-3 py-2"
              data-testid="crm-outreach-instructions"
              maxLength={1000}
            />
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={saveActivity}
              onChange={(e) => setSaveActivity(e.target.checked)}
              className="rounded border-slate-300"
              data-testid="crm-outreach-save-activity"
            />
            Log this draft to the contact's activity history
          </label>

          {/* Generate button (no result yet) */}
          {!result && (
            <Button
              onClick={() => generate(false)}
              disabled={busy}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="crm-outreach-generate-btn"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting…</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate outreach</>
              )}
            </Button>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-4" data-testid="crm-outreach-result">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Draft · {result.channel} · {result.model}
                </span>
                <span className="text-xs text-slate-400">
                  Tone: {result.tone}
                </span>
              </div>

              {result.subject && (
                <Field
                  label="Subject"
                  text={result.subject}
                  copyKey="subject"
                  copied={copied}
                  onCopy={copy}
                />
              )}

              <Field
                label={`Short version (${result.short.split(/\s+/).filter(Boolean).length} words)`}
                text={result.short}
                copyKey="short"
                copied={copied}
                onCopy={copy}
                multiline
              />

              {result.long && (
                <Field
                  label={`Long version (${result.long.split(/\s+/).filter(Boolean).length} words)`}
                  text={result.long}
                  copyKey="long"
                  copied={copied}
                  onCopy={copy}
                  multiline
                />
              )}

              <Field
                label="Call to action"
                text={result.cta}
                copyKey="cta"
                copied={copied}
                onCopy={copy}
              />

              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                Suggested follow-up in <strong>{result.follow_up_days} days</strong> if no reply.
              </div>

              {/* Send-via-email block */}
              {channel !== "linkedin" && (
                <div
                  className="bg-cyan-50 border border-cyan-200 rounded-md px-3 py-3 space-y-2"
                  data-testid="crm-outreach-send-block"
                >
                  {sentInfo ? (
                    <div className="text-sm text-emerald-800 flex items-center gap-2" data-testid="crm-outreach-sent-confirmation">
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <div>
                        <strong>Email sent</strong> to {contact.email}
                        {sentInfo.next_follow_up_at && (
                          <span className="text-xs text-emerald-700 block">
                            Follow-up scheduled for {new Date(sentInfo.next_follow_up_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : !contact.email ? (
                    <div className="text-xs text-amber-700">
                      Contact has no email — add one to enable sending.
                    </div>
                  ) : sandboxBlocked ? (
                    <div
                      className="flex items-start gap-2 text-sm text-rose-800 bg-rose-50 -m-1 px-3 py-2 rounded"
                      data-testid="crm-outreach-sandbox-blocked"
                    >
                      <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs leading-relaxed">
                        <strong>Sandbox: send blocked.</strong> Resend is in test mode and only{" "}
                        <strong className="font-mono">{senderStatus?.verified_recipients?.[0]}</strong>{" "}
                        can receive sent emails right now. Sending to{" "}
                        <strong className="font-mono">{contact.email}</strong> would fail.{" "}
                        <a
                          href={senderStatus?.verify_url || "https://resend.com/domains"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline font-semibold"
                          data-testid="crm-outreach-sandbox-verify"
                        >
                          Verify your domain at Resend
                        </a>{" "}
                        to send to all contacts. You can still copy the draft above and send manually.
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-xs text-slate-600 flex items-center justify-between gap-2 flex-wrap">
                        <span>
                          Will send to <strong className="text-slate-800">{contact.email}</strong> via Resend.
                        </span>
                        {result.long && (
                          <label className="inline-flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useLong}
                              onChange={(e) => setUseLong(e.target.checked)}
                              className="rounded border-slate-300"
                              data-testid="crm-outreach-use-long"
                            />
                            Use long version
                          </label>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {result && (
          <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex flex-wrap gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => copy("all", buildFullText(result))}
              data-testid="crm-outreach-copy-all"
            >
              {copied === "all" ? <Check className="w-4 h-4 mr-1.5 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1.5" />}
              Copy all
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => generate(true)}
              disabled={busy}
              data-testid="crm-outreach-regenerate"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Regenerate
            </Button>
            {!saveActivity && (
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/crm/contacts/${contact.id}/activities`, {
                      type: "AI_MESSAGE_GENERATED",
                      title: `AI outreach drafted (${result.channel})`,
                      body: `Subject: ${result.subject}\n\n${result.short}`,
                    });
                    toast.success("Saved to activity");
                    if (onGenerated) onGenerated();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Save failed");
                  }
                }}
                className="bg-cyan-700 hover:bg-cyan-800 text-white"
                data-testid="crm-outreach-save-history"
              >
                <Save className="w-4 h-4 mr-1.5" /> Save to history
              </Button>
            )}
            {canSend && !sentInfo && (
              <Button
                size="sm"
                onClick={sendEmail}
                disabled={sending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="crm-outreach-send-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-1.5" />
                )}
                Send via email
              </Button>
            )}
            <Button
              size="sm"
              onClick={onClose}
              className="bg-slate-800 hover:bg-slate-900 text-white"
              data-testid="crm-outreach-done"
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function buildFullText(r) {
  const lines = [];
  if (r.subject) lines.push(`Subject: ${r.subject}`);
  lines.push("");
  lines.push(r.long || r.short);
  if (r.cta) {
    lines.push("");
    lines.push(r.cta);
  }
  return lines.join("\n");
}

function Field({ label, text, copyKey, copied, onCopy, multiline }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</span>
        <button
          onClick={() => onCopy(copyKey, text)}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          data-testid={`crm-outreach-copy-${copyKey}`}
        >
          {copied === copyKey ? (
            <><Check className="w-3.5 h-3.5 text-emerald-600" /> Copied</>
          ) : (
            <><Copy className="w-3.5 h-3.5" /> Copy</>
          )}
        </button>
      </div>
      <div
        className={`text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 ${
          multiline ? "whitespace-pre-wrap" : "truncate"
        }`}
      >
        {text}
      </div>
    </div>
  );
}
